/* Change only the selected cart line, keeping its quantity and properties intact. */
if (!customElements.get('potion-cart-subscription')) {
  customElements.define('potion-cart-subscription', class extends HTMLElement {
    connectedCallback() {
      this.select = this.querySelector('select');
      this.button = this.querySelector('button');
      this.status = this.querySelector('[role="status"]');
      this.addEventListener('change', (event) => {
        // Dawn handles quantity changes at the cart-items level.
        event.stopPropagation();
        this.button.disabled = this.select.value === this.dataset.currentPlan;
        this.button.textContent = this.select.value === '0' ? 'Switch to one-time purchase' :
          (this.dataset.currentPlan === '0' ? 'Switch to subscription' : 'Update subscription');
        this.status.textContent = '';
      });
      this.button.addEventListener('click', () => this.updatePlan());
    }

    async updatePlan() {
      if (this.busy || this.select.value === this.dataset.currentPlan) return;
      this.busy = true;
      const cartItems = this.closest('cart-items, cart-drawer-items');
      const contextTag = cartItems.tagName;
      // The full cart page also contains a hidden drawer. Keep both in sync.
      const cartViews = new Set([cartItems, ...document.querySelectorAll('cart-items, cart-drawer-items')]);
      const sections = Array.from(new Map(Array.from(cartViews).flatMap((view) => view.getSectionsToRender()).map((section) => [section.id, section])).values());
      const plan = this.select.value;
      this.button.disabled = true;
      this.select.disabled = true;
      this.setAttribute('aria-busy', 'true');
      this.status.textContent = 'Updating your purchase option…';
      // Prevent another line mutation or checkout while the request is in flight.
      const controls = Array.from(document.querySelectorAll('cart-items button, cart-items input, cart-items select, cart-drawer button, cart-drawer input, cart-drawer select, [name="checkout"]'));
      const enabledControls = controls.filter((control) => !control.disabled);
      enabledControls.forEach((control) => { control.disabled = true; });
      const deferred = cartItems.createCartLinesUpdateEvent?.('update', this.closest('.cart-item')?.querySelector('[data-quantity-variant-id]')?.dataset.quantityVariantId, Number(this.dataset.quantity), this.dataset.lineKey);
      let changed = false;
      try {
        // Selling-plan changes require Shopify's line index. Resolve it from the current
        // key immediately before changing it, rather than trusting a stale rendered index.
        const snapshotResponse = await fetch(`${routes.cart_url}.js`);
        if (!snapshotResponse.ok) throw new Error('Could not read your cart. Please try again.');
        const snapshot = await snapshotResponse.json();
        const index = snapshot.items.findIndex((item) => item.key === this.dataset.lineKey);
        if (index < 0) throw new Error('Your cart has changed. Refresh the cart and try again.');
        const response = await fetch(routes.cart_change_url, {
          ...fetchConfig(),
          body: JSON.stringify({
            line: index + 1,
            quantity: snapshot.items[index].quantity,
            selling_plan: plan === '0' ? null : Number(plan),
            sections: sections.map((section) => section.section),
            sections_url: window.location.pathname,
          }),
        });
        const cart = await response.json();
        if (!response.ok || cart.errors || cart.status) throw new Error(cart.description || cart.message || 'Could not update your purchase option. Please try again.');
        changed = true;
        cartItems.resolveCartLinesUpdate?.(deferred, cart);
        // Parse all sections before replacing any, so a partial response cannot leave stale totals.
        const replacements = sections.map((section) => {
          const html = cart.sections?.[section.section];
          if (!html) throw new Error('Missing cart section');
          const source = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
          const root = document.getElementById(section.id);
          const target = root?.querySelector(section.selector) || root;
          if (!source || !target) throw new Error('Missing cart content');
          return { target, html: source.innerHTML };
        });
        replacements.forEach(({ target, html }) => { target.innerHTML = html; });
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: cart });
        const updated = Array.from(document.querySelectorAll('potion-cart-subscription')).find((offer) => offer.dataset.currentPlan === plan && offer.closest('cart-items, cart-drawer-items')?.tagName === contextTag);
        if (updated) {
          updated.querySelector('[role="status"]').textContent = plan === '0' ? 'Updated to one-time purchase.' : 'Subscription updated.';
          updated.querySelector('select').focus();
        }
      } catch (error) {
        // A successful change with missing sections must refresh, never retry the mutation.
        if (changed) { window.location.reload(); return; }
        this.status.textContent = error.message || 'Could not update your purchase option. Please try again.';
        cartItems.dispatchCartErrorEvent?.(this.status.textContent, 'SERVICE_UNAVAILABLE');
        deferred?.reject(error);
      } finally {
        enabledControls.forEach((control) => { if (control.isConnected) control.disabled = false; });
        this.busy = false;
        this.select.disabled = false;
        this.button.disabled = this.select.value === this.dataset.currentPlan;
        this.removeAttribute('aria-busy');
      }
    }
  });
}
